import React, { useState } from 'react';
import { useLabelAttributes } from '../hooks/useLabelAttributes';

interface ObjectEditModalProps {
  object: {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    color?: string;
    shapes?: string[];
    hotkey?: string;
    category?: string; // NEW: top-level category (optional)
    subcategory?: string; // RENAMED FROM category: subcategory (required)
    attributes?: string[];
    labelType: string;
    source: 'global' | 'schema';
  };
  categories: any[]; // subcategories
  parentCategories: any[]; // parent categories
  onSave: (objectData: any) => Promise<void>;
  onCancel: () => void;
}

export default function ObjectEditModal({ object, categories, parentCategories, onSave, onCancel }: ObjectEditModalProps) {
  const { attributes: labelAttributes, loading: attributesLoading } = useLabelAttributes();
  const instanceAttributes = labelAttributes.filter(
    (attr) => attr.level === 'instance' && attr.active !== false
  );

  const [formData, setFormData] = useState({
    name: object.name,
    displayName: object.displayName,
    description: object.description || '',
    color: object.color || '#3b82f6',
    shapes: object.shapes || ['rectangle'],
    hotkey: object.hotkey || '',
    category: object.category || '', // NEW: top-level category (optional)
    subcategory: object.subcategory || object.category || '', // RENAMED: subcategory (required, fallback to old category for migration)
    attributes: object.attributes || ([] as string[])
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
  ];

  const toggleAttribute = (attrName: string) => {
    setFormData((prev) => {
      const current = prev.attributes || [];
      const next = current.includes(attrName)
        ? current.filter((name) => name !== attrName)
        : [...current, attrName];
      return { ...prev, attributes: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.displayName || !formData.subcategory) {
      alert('Please fill in all required fields (Object ID, Display Name, and Subcategory)');
      return;
    }
    
    // Ensure shapes array is not empty
    if (!formData.shapes || formData.shapes.length === 0) {
      alert('Please select at least one shape');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              Edit {object.labelType === 'Global Object' ? 'Global Object' : 'Schema Object'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Object ID *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., cutting_board, knife, plate"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Internal ID (lowercase, underscores)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Cutting Board, Knife, Plate"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">User-friendly name</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the object"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category (optional)
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None (Uncategorized)</option>
                  {parentCategories.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.displayName}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Top-level category for grouping</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subcategory *
                </label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a subcategory...</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.displayName}
                    </option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    No subcategories available. Create subcategories first.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attributes
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Attributes selected here apply to this object everywhere it is used.
              </p>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {attributesLoading ? (
                  <p className="text-xs text-gray-500">Loading attributes...</p>
                ) : instanceAttributes.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    No instance-level attributes available. Create them in Attributes admin.
                  </p>
                ) : (
                  instanceAttributes.map((attr) => {
                    const checked = formData.attributes.includes(attr.name);
                    return (
                      <label key={attr.name} className="flex items-start space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttribute(attr.name)}
                          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="text-sm text-gray-900">
                            {attr.displayName}{' '}
                            <span className="font-mono text-xs text-gray-500">{attr.name}</span>
                          </span>
                          {attr.description && (
                            <span className="block text-xs text-gray-500 mt-0.5">{attr.description}</span>
                          )}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Shapes
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['rectangle', 'polygon', 'point', 'line'].map((shape) => (
                    <label key={shape} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.shapes.includes(shape)}
                        onChange={(e) => {
                          const currentShapes = formData.shapes;
                          const newShapes = e.target.checked
                            ? [...currentShapes, shape]
                            : currentShapes.filter(s => s !== shape);
                          setFormData({ ...formData, shapes: newShapes });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{shape}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hotkey (optional)
                </label>
                <input
                  type="text"
                  value={formData.hotkey}
                  onChange={(e) => setFormData({ ...formData, hotkey: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1, 2, 3, k"
                />
                <p className="text-xs text-gray-500 mt-1">Keyboard shortcut for quick selection</p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color ? 'border-gray-400' : 'border-gray-200'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
